import { z } from 'zod/v4';
import { MASTRA_THREAD_ID_KEY } from '../../request-context';
import type { RequestContext } from '../../request-context';
import { createTool } from '../../tools';
import type { Tool } from '../../tools';
import { BM25Index } from '../../workspace/search/bm25';
import type { TokenizeOptions } from '../../workspace/search/bm25';
import type { ProcessInputStepArgs, Processor } from '../index';

export type ToolSearchFilterPhase = 'search' | 'load' | 'active';

export type ToolSearchFilterArgs = {
  /** The resolved tool id. */
  toolName: string;
  tool: Tool<any, any>;
  requestContext?: RequestContext;
  phase: ToolSearchFilterPhase;
};

/**
 * Thread state with timestamp for TTL management
 */
interface ThreadState {
  tools: Set<string>;
  lastAccessed: number;
}

/**
 * Configuration options for ToolSearchProcessor
 */
export interface ToolSearchProcessorOptions {
  /**
   * All tools that can be searched and loaded dynamically.
   * These tools are not immediately available - they must be discovered via search and loaded on demand.
   */
  tools: Record<string, Tool<any, any>>;

  /**
   * Configuration for the search behavior
   */
  search?: {
    /**
     * Maximum number of tools to return in search results
     * @default 5
     */
    topK?: number;

    /**
     * Minimum relevance score (0-1) for including a tool in search results
     * @default 0
     */
    minScore?: number;
  };

  /**
   * Time-to-live for thread state in milliseconds.
   * After this duration of inactivity, thread state will be eligible for cleanup.
   * Set to 0 to disable TTL cleanup.
   * @default 3600000 (1 hour)
   */
  ttl?: number;

  /**
   * Optional request-aware hook for filtering tools during search, load, and active tool injection.
   * Return false to hide or block a tool for the current request.
   */
  filter?: (args: ToolSearchFilterArgs) => boolean | Promise<boolean>;
}

/**
 * Search result with ranking score
 */
interface SearchResult {
  name: string;
  description: string;
  score: number;
}

/**
 * Tokenization options tuned for tool names and descriptions.
 * Splits on underscores, hyphens, and punctuation (common in tool IDs).
 * No stopwords filtering since tool descriptions are short.
 */
const TOOL_SEARCH_TOKENIZE_OPTIONS: TokenizeOptions = {
  lowercase: true,
  removePunctuation: false,
  minLength: 2,
  stopwords: new Set(),
  splitPattern: /[\s\-_.,;:!?()[\]{}'"]+/,
};

/**
 * Processor that enables dynamic tool discovery and loading.
 *
 * Instead of providing all tools to the agent upfront, this processor:
 * 1. Gives the agent two meta-tools: search_tools and load_tool
 * 2. Agent searches for relevant tools using keywords
 * 3. Agent loads specific tools into the conversation on demand
 * 4. Loaded tools become immediately available for use
 *
 * This pattern dramatically reduces context usage when working with many tools (100+).
 *
 * @example
 * ```typescript
 * const toolSearch = new ToolSearchProcessor({
 *   tools: {
 *     createIssue: githubTools.createIssue,
 *     sendEmail: emailTools.send,
 *     // ... 100+ tools
 *   },
 *   search: { topK: 5, minScore: 0 },
 *   ttl: 3600000, // 1 hour (default)
 * });
 *
 * const agent = new Agent({
 *   name: 'my-agent',
 *   inputProcessors: [toolSearch],
 *   tools: {}, // Always-available tools (if any)
 * });
 * ```
 */
export class ToolSearchProcessor implements Processor<'tool-search'> {
  readonly id = 'tool-search';
  readonly name = 'Tool Search Processor';
  readonly description = 'Enables dynamic tool discovery and loading via search';

  private allTools: Record<string, Tool<any, any>>;
  private searchConfig: Required<NonNullable<ToolSearchProcessorOptions['search']>>;
  private ttl: number;
  private filter?: ToolSearchProcessorOptions['filter'];

  /** BM25 index for tool search */
  private bm25Index: BM25Index;
  /** Map from tool ID to full description (for result formatting) */
  private toolDescriptions = new Map<string, string>();

  /**
   * Thread-scoped state management for loaded tools with TTL support.
   * Instance-scoped to prevent cross-processor interference.
   * Maps threadId -> ThreadState (tools + timestamp)
   */
  private threadLoadedTools = new Map<string, ThreadState>();

  constructor(options: ToolSearchProcessorOptions) {
    this.allTools = options.tools;
    this.filter = options.filter;
    this.searchConfig = {
      topK: options.search?.topK ?? 5,
      minScore: options.search?.minScore ?? 0,
    };
    this.ttl = options.ttl ?? 3600000; // Default: 1 hour

    // Create BM25 index with tool-search-specific tokenization
    this.bm25Index = new BM25Index({}, TOOL_SEARCH_TOKENIZE_OPTIONS);

    // Index all tools
    this.indexTools();

    // Start periodic cleanup if TTL is enabled
    if (this.ttl > 0) {
      this.scheduleCleanup();
    }
  }

  /**
   * Get the thread ID from the request context, or use 'default' as fallback.
   */
  private getThreadId(args: ProcessInputStepArgs): string {
    return args.requestContext?.get(MASTRA_THREAD_ID_KEY) || 'default';
  }

  /**
   * Get the set of loaded tool names for the current thread.
   * Updates the lastAccessed timestamp for TTL management.
   */
  private getLoadedToolNames(threadId: string): Set<string> {
    if (!this.threadLoadedTools.has(threadId)) {
      this.threadLoadedTools.set(threadId, {
        tools: new Set(),
        lastAccessed: Date.now(),
      });
    }
    const state = this.threadLoadedTools.get(threadId)!;
    state.lastAccessed = Date.now(); // Update timestamp on access
    return state.tools;
  }

  private findToolById(toolId: string): Tool<any, any> | undefined {
    return Object.values(this.allTools).find(tool => tool.id === toolId);
  }

  private findToolForDynamicName(toolName: string): Tool<any, any> | undefined {
    const toolByKey = this.allTools[toolName];
    const toolById = this.findToolById(toolName);
    return this.filter ? (toolById ?? toolByKey) : (toolByKey ?? toolById);
  }

  private async isToolAllowed(
    tool: Tool<any, any>,
    requestContext: RequestContext | undefined,
    phase: ToolSearchFilterPhase,
  ): Promise<boolean> {
    if (!this.filter) {
      return true;
    }

    try {
      return await this.filter({ toolName: tool.id, tool, requestContext, phase });
    } catch {
      return false;
    }
  }

  private async getSuggestedToolNames(toolName: string, requestContext?: RequestContext): Promise<string[]> {
    const matchesToolName = (name: string) =>
      name.toLowerCase().includes(toolName.toLowerCase()) || toolName.toLowerCase().includes(name.toLowerCase());

    if (!this.filter) {
      return Object.keys(this.allTools).filter(matchesToolName);
    }

    const allowedNames: string[] = [];

    for (const name of Object.keys(this.allTools)) {
      if (!matchesToolName(name)) continue;

      const tool = this.findToolForDynamicName(name);
      if (!tool) continue;

      const isAllowed = await this.isToolAllowed(tool, requestContext, 'load');
      if (isAllowed) {
        allowedNames.push(name);
        if (allowedNames.length >= 3) break;
      }
    }

    return allowedNames;
  }

  /**
   * Get loaded tools as Tool objects for the current thread.
   */
  private async getLoadedTools(
    threadId: string,
    requestContext?: RequestContext,
  ): Promise<Record<string, Tool<any, any>>> {
    const loadedNames = this.getLoadedToolNames(threadId);
    const loadedTools: Record<string, Tool<any, any>> = {};

    for (const toolName of loadedNames) {
      const tool = this.findToolForDynamicName(toolName);
      if (tool) {
        const isAllowed = await this.isToolAllowed(tool, requestContext, 'active');
        if (isAllowed) {
          loadedTools[toolName] = tool;
        }
      }
    }

    return loadedTools;
  }

  /**
   * Get loaded tools for the given request context.
   * Used by agent resume paths to rebuild tool executors after approval suspension.
   */
  public getLoadedToolsForRequestContext(args?: {
    requestContext?: RequestContext;
  }): Promise<Record<string, Tool<any, any>>> {
    const threadId = (args?.requestContext?.get(MASTRA_THREAD_ID_KEY) as string | undefined) || 'default';
    return this.getLoadedTools(threadId, args?.requestContext);
  }

  /**
   * Clear loaded tools for a specific thread (useful for testing).
   *
   * @param threadId - The thread ID to clear, or 'default' if not provided
   */
  public clearState(threadId: string = 'default'): void {
    this.threadLoadedTools.delete(threadId);
  }

  /**
   * Clear all thread state for this processor instance (useful for testing).
   */
  public clearAllState(): void {
    this.threadLoadedTools.clear();
  }

  /**
   * Clean up stale thread state based on TTL.
   * Removes threads that haven't been accessed within the TTL period.
   *
   * @returns Number of threads cleaned up
   */
  private cleanupStaleState(): number {
    if (this.ttl <= 0) return 0; // TTL disabled

    const now = Date.now();
    let cleanedCount = 0;

    for (const [threadId, state] of this.threadLoadedTools.entries()) {
      if (now - state.lastAccessed > this.ttl) {
        this.threadLoadedTools.delete(threadId);
        cleanedCount++;
      }
    }

    return cleanedCount;
  }

  /**
   * Schedule periodic cleanup of stale thread state.
   * Runs cleanup every TTL/2 milliseconds to prevent unbounded memory growth.
   */
  private scheduleCleanup(): void {
    // Clean up at half the TTL interval
    const cleanupInterval = Math.max(this.ttl / 2, 60000); // Minimum 1 minute

    // Use setInterval but don't block process exit
    const intervalId = setInterval(() => {
      this.cleanupStaleState();
    }, cleanupInterval);

    // Allow process to exit even if interval is active
    if (intervalId.unref) {
      intervalId.unref();
    }
  }

  /**
   * Get statistics about current thread state (useful for monitoring).
   *
   * @returns Object with thread count and oldest access time
   */
  public getStateStats(): { threadCount: number; oldestAccessTime: number | null } {
    if (this.threadLoadedTools.size === 0) {
      return { threadCount: 0, oldestAccessTime: null };
    }

    let oldest = Date.now();
    for (const state of this.threadLoadedTools.values()) {
      if (state.lastAccessed < oldest) {
        oldest = state.lastAccessed;
      }
    }

    return {
      threadCount: this.threadLoadedTools.size,
      oldestAccessTime: oldest,
    };
  }

  /**
   * Manually trigger cleanup of stale state (useful for testing and monitoring).
   *
   * @returns Number of threads cleaned up
   */
  public cleanupNow(): number {
    return this.cleanupStaleState();
  }

  /**
   * Index all tools into the BM25 index
   */
  private indexTools(): void {
    for (const tool of Object.values(this.allTools)) {
      const name = tool.id;
      const description = tool.description || '';
      this.bm25Index.add(name, `${name} ${description}`);
      this.toolDescriptions.set(name, description);
    }
  }

  /**
   * Search for tools matching the query using BM25 ranking
   * with name-match boosting.
   *
   * @param query - Search keywords
   * @returns Array of matching tools with scores, sorted by relevance
   */
  private async searchTools(query: string, requestContext?: RequestContext): Promise<SearchResult[]> {
    if (this.bm25Index.size === 0) return [];

    // Get BM25 results (request more than topK to allow for re-ranking after boosting).
    // When filtering is enabled, inspect every BM25 match so denied high-ranking tools
    // do not prevent lower-ranking allowed tools from filling the result set.
    const searchLimit = this.filter ? this.bm25Index.size : this.searchConfig.topK * 2;
    const bm25Results = this.bm25Index.search(query, searchLimit, 0);

    if (bm25Results.length === 0) return [];

    // Apply name-match boosting on top of BM25 scores
    const queryTokens = query
      .toLowerCase()
      .split(/[\s\-_.,;:!?()[\]{}'"]+/)
      .filter(t => t.length > 1);

    const boostedResults = bm25Results.map(result => {
      let score = result.score;
      const nameLower = result.id.toLowerCase();

      for (const term of queryTokens) {
        if (nameLower === term) {
          score += 5;
        } else if (nameLower.includes(term)) {
          score += 2;
        }
      }

      return { id: result.id, score };
    });

    const filteredResults: typeof boostedResults = [];
    for (const result of boostedResults.sort((a, b) => b.score - a.score)) {
      if (result.score <= this.searchConfig.minScore) continue;

      const tool = this.findToolById(result.id);
      if (!tool) continue;

      const isAllowed = await this.isToolAllowed(tool, requestContext, 'search');
      if (isAllowed) {
        filteredResults.push(result);
        if (filteredResults.length >= this.searchConfig.topK) break;
      }
    }

    // Apply topK and format results.
    return filteredResults.slice(0, this.searchConfig.topK).map(r => {
      const description = this.toolDescriptions.get(r.id) || '';
      return {
        name: r.id,
        description: description.length > 150 ? description.slice(0, 147) + '...' : description,
        score: Math.round(r.score * 100) / 100,
      };
    });
  }

  async processInputStep(args: ProcessInputStepArgs) {
    const { tools, messageList } = args;
    const threadId = this.getThreadId(args);
    const loadedToolNames = this.getLoadedToolNames(threadId);

    // Add system instruction about the meta-tools
    messageList.addSystem(
      'To discover available tools, call search_tools with a keyword query. ' +
        'To add one or more tools to the conversation, call load_tool with a toolName or toolNames array. ' +
        'Tools must be loaded before they can be used.',
    );

    // Create the search tool with BM25 ranking
    const searchTool = createTool({
      id: 'search_tools',
      description:
        'Search for available tools by keyword. ' +
        "Use this when you need a capability you don't currently have. " +
        'Returns a list of matching tools with their names and descriptions. ' +
        'After finding a useful tool, use load_tool to make it available.',
      inputSchema: z.object({
        query: z.string().describe('Search keywords (e.g., "weather", "github issue", "database query")'),
      }),
      outputSchema: z.object({
        results: z.array(
          z.object({
            name: z.string(),
            description: z.string(),
            score: z.number(),
          }),
        ),
        message: z.string(),
      }),
      execute: async ({ query }) => {
        // Use BM25 search for relevance-ranked results
        const results = await this.searchTools(query, args.requestContext);

        if (results.length === 0) {
          return {
            results: [],
            message: `No tools found matching "${query}". Try different keywords.`,
          };
        }

        return {
          results,
          message: `Found ${results.length} tool(s). Use load_tool with an exact toolName or a toolNames array to make them available.`,
        };
      },
    });

    // Create the load tool that uses thread-scoped state
    const loadTool = createTool({
      id: 'load_tool',
      description:
        'Load one or more tools into your context. ' +
        'Call this after finding tools with search_tools. ' +
        'Once loaded, tools will be available for use. ' +
        'Pass a single toolName or an array of toolNames to load multiple tools at once.',
      inputSchema: z.object({
        toolName: z.string().optional().describe('The exact name of a tool to load (from search results)'),
        toolNames: z
          .array(z.string())
          .optional()
          .describe('Array of exact tool names to load in one call (from search results)'),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        message: z.string(),
        loadedCount: z.number().optional(),
        toolName: z.string().optional(),
        loaded: z.array(z.string()).optional(),
        notFound: z.array(z.string()).optional(),
        alreadyLoaded: z.array(z.string()).optional(),
      }),
      execute: async ({ toolName, toolNames }) => {
        // Determine which tools to load
        let toLoad: string[];
        const toolNamesProvided = toolNames !== undefined;
        if (toolNamesProvided && toolNames!.length === 0 && !toolName) {
          return {
            success: false,
            message: 'toolNames array must not be empty.',
          };
        }
        if (toolNamesProvided && toolNames!.length > 0) {
          // Merge toolName into toolNames if both provided, then dedupe
          const base: string[] = [...toolNames!];
          if (toolName) base.push(toolName);
          toLoad = Array.from(new Set(base));
        } else if (toolName) {
          toLoad = [toolName];
        } else {
          return {
            success: false,
            message: 'You must provide either toolName (string) or toolNames (array) to load.',
          };
        }

        const notFound: string[] = [];
        const alreadyLoaded: string[] = [];
        const loaded: string[] = [];

        for (const name of toLoad) {
          // Check if tool exists
          const matchingTool = this.findToolForDynamicName(name);

          if (!matchingTool) {
            notFound.push(name);
            continue;
          }

          const isAllowed = await this.isToolAllowed(matchingTool, args.requestContext, 'load');
          if (!isAllowed) {
            notFound.push(name);
            continue;
          }

          // Check if already loaded (thread-scoped)
          if (loadedToolNames.has(name)) {
            alreadyLoaded.push(name);
            continue;
          }

          // Load the tool (thread-scoped)
          loadedToolNames.add(name);
          loaded.push(name);
        }

        // Build response based on how many tools were requested
        // Only use single-tool backward-compatible shape when using the legacy toolName param
        if (toLoad.length === 1 && !toolNamesProvided) {
          // Single-tool response (backward compatible shape)
          if (notFound.length > 0) {
            const name = toLoad[0]!;
            const suggestions = await this.getSuggestedToolNames(name, args.requestContext);
            let message = `Tool "${name}" not found.`;
            if (suggestions.length > 0) {
              message += ` Did you mean: ${suggestions.slice(0, 3).join(', ')}?`;
            } else {
              message += ' Use search_tools to find available tools.';
            }
            return { success: false, message, toolName: name };
          }
          if (alreadyLoaded.length > 0) {
            return {
              success: true,
              message: `Tool "${alreadyLoaded[0]}" is already loaded and available.`,
              toolName: alreadyLoaded[0],
            };
          }
          return {
            success: true,
            message: `Tool "${loaded[0]}" loaded successfully. It will be available on your next turn.`,
            toolName: loaded[0],
          };
        }

        // Multi-tool response
        const parts: string[] = [];
        if (loaded.length > 0) parts.push(`Loaded: ${loaded.join(', ')} — available on your next turn`);
        if (alreadyLoaded.length > 0) parts.push(`Already loaded: ${alreadyLoaded.join(', ')}`);
        if (notFound.length > 0) parts.push(`Not found: ${notFound.join(', ')}`);

        return {
          success: notFound.length === 0,
          message: parts.join(' | '),
          loadedCount: loaded.length,
          loaded: loaded.length > 0 ? loaded : undefined,
          notFound: notFound.length > 0 ? notFound : undefined,
          alreadyLoaded: alreadyLoaded.length > 0 ? alreadyLoaded : undefined,
        };
      },
    });

    // Get loaded tools for this thread
    const loadedTools = await this.getLoadedTools(threadId, args.requestContext);

    // Return merged tools: meta-tools + existing tools + loaded tools
    return {
      tools: {
        search_tools: searchTool,
        load_tool: loadTool,
        ...(tools ?? {}),
        ...loadedTools,
      },
    };
  }
}
